import type {Delimiter, ListItem, Nodes} from 'mdast'
import type {Extension} from 'mdast-util-from-markdown'
import {visit} from 'unist-util-visit'

import {pushChild, removeChild, unshiftChild} from '../tree-manipulation-utils'

/**
 * In HTML, lists (`ul` and `ol`) may _only_ contain list items (`li`). But in mdast by default, the list node will
 * include all tokens that appear between the list items, such as line endings, list item delimiters, and even blockquote
 * delimiters. To resolve this and allow us to easily generate a valid HTML structure, we push all of these things into
 * the corresponding list item. Most delimiters get pushed into the following item, except for line endings (which get
 * pushed into the preceding item) and anything at the end of the list (which gets pushed into the last item).
 *
 * @example
 * Before:
 * ```
 * list
 *   delimiter_listItemPrefix
 *   listItem
 *     content
 *   delimiter_lineEnding
 *   delimiter_listItemPrefix
 *   listItem
 *     content
 *   delimiter_lineEnding
 * ```
 *
 * After:
 * ```
 * list
 *   listItem
 *     delimiter_listItemPrefix
 *     content
 *     delimiter_lineEnding
 *   listItem
 *     delimiter_listItemPrefix
 *     content
 *     delimiter_lineEnding
 * ```
 */
export function mdastExpandListItems(): Extension {
  return {
    transforms: [expandListItems],
  }
}

function expandListItems(tree: Nodes): void {
  visit(tree, 'list', list => {
    /** As we iterate through the children backwards, this will be the last item we encountered. */
    let nextListItem: ListItem | null = null
    /** Stack of nodes to push to the next item we encounter as we iterate through the children backwards. */
    let nodesToPushToPreviousItem: Delimiter[] = []

    // Going from back to front makes it easier to manage indexes because they won't change as we remove items
    for (let i = list.children.length - 1; i >= 0; i--) {
      const child = list.children[i]
      if (!child) continue

      if (child.type === 'listItem') {
        nextListItem = child

        // Append all of the `nodesToPushToPreviousItem` to the end of the item, in reverse order
        for (const item of nodesToPushToPreviousItem.reverse()) {
          pushChild(nextListItem, item)
          nodesToPushToPreviousItem = []
        }
      } else if (
        // Items at the very end of a list (while `nextListItem` is null) will all get pushed into the last item in the list
        nextListItem === null ||
        // Line endings immediately following a list item will get pushed into that item
        (child.tokenType === 'lineEnding' && list.children[i - 1]?.type === 'listItem')
      ) {
        removeChild(list, i)
        nodesToPushToPreviousItem.push(child)
      } else {
        // Everything else gets unshifted into the beginning of the list item that follows it
        removeChild(list, i)
        unshiftChild(nextListItem, child)
      }
    }

    // It should be impossible now for `nodesToPushToPreviousItem` to have anything in it, because we only push things
    // to it at the end of the list and when we know there is an adjacent previous item. And a list has to contain at
    // least one item to exist at all.
  })
}
